import React, { useEffect, useMemo, useState } from 'react';
import type { Office, User } from '../types';

import UltimatePagination from 'react-ultimate-pagination-bootstrap-4';
import { isEmpty } from 'lodash';
import UserTableRow from './row';
import OfficeBadge from '../shared/office_badge';
import Filters from './filters';

type UserTableProps = {
  initialSelectedUserIds: number[];
  users: User[];
  officesById: { [key: number]: Office };
}
const PER_PAGE = 10;

const UserTable = ({ initialSelectedUserIds, users, officesById } : UserTableProps) => {
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [officeIds, setOfficeIds] = useState<Array<number>>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>(initialSelectedUserIds || []);

  const handleUserSelection = (userId: number, isSelected: boolean) => {
    if (isSelected) {
      setSelectedUserIds([...selectedUserIds, userId]);
    } else {
      setSelectedUserIds(selectedUserIds.filter(id => id !== userId));
    }
  };

  useEffect(() => {
    setPage(1);
  }, [searchTerm, officeIds]);

  const sortedUsers = useMemo(() => {
    return [
      ...users.filter(user => selectedUserIds.includes(user.id)), // selected are first
      ...users.filter(user => !selectedUserIds.includes(user.id)) // the rest are last
    ];
  }, [users]);

  const filteredUsers = useMemo(() => {
    return sortedUsers.filter((user) => {
      if (!isEmpty(searchTerm)) {
        const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
        if (!fullName.includes(searchTerm.toLowerCase())) return false;
      }

      if (officeIds.length > 0 && !officeIds.includes(user.companyOfficeId)) return false;

      return true;
    });
  }, [sortedUsers, searchTerm, officeIds]);

  const totalPages = useMemo(() => {
    return Math.ceil(filteredUsers.length / PER_PAGE) || 1;
  }, [filteredUsers]);

  const usersForDisplay = useMemo(() => {
    const start = (page - 1) * PER_PAGE;
    const end = start + PER_PAGE;
    return filteredUsers.slice(start, end);
  }, [filteredUsers, page]);

  const officeOptions = useMemo(() => {
    return Object.keys(officesById).map(officeId => ({
      value: officeId,
      label: officesById[officeId].name,
    })).filter(option => !officeIds.includes(parseInt(option.value)));
  }, [officesById, officeIds]);

  const handleOfficeSelect = (selectedOption) => {
    if (selectedOption) {
      setOfficeIds([...officeIds, parseInt(selectedOption.value)]);
    } else {
      setOfficeIds(officeIds.filter(id => id !== parseInt(selectedOption.value)));
    }
  };

  const toggleSelectAll = (isChecked) => {
    const allUserIds = usersForDisplay.map(user => user.id);
    if (isChecked) {
      setSelectedUserIds([...selectedUserIds, ...allUserIds]);
    } else {
      setSelectedUserIds(selectedUserIds.filter(id => !allUserIds.includes(id)));
    }
  };

  return (
    <div>
      <Filters
        officeOptions={officeOptions}
        searchTerm={searchTerm}
        onSelectOffice={handleOfficeSelect}
        onSetSearchTerm={setSearchTerm}
      />
      <div className="mb-3 d-flex gap-2">
        {officeIds.map(officeId => (
          <OfficeBadge
            key={officeId}
            officeName={officesById[officeId].name}
            onClick={() => setOfficeIds(officeIds.filter(id => id !== officeId))}
          />
        ))}
      </div>
      {usersForDisplay.length > 0 ? (
        <div>
          <h5 className="mb-3">
            Selected Users {selectedUserIds.length > 0 && `(${selectedUserIds.length})`}
          </h5>
          <div className="table-container">
            <table className="table table-bordered">
              <thead className="bg-light">
                <tr>
                  <th>
                    <div className="d-flex gap-2 align-items-center flex-direction-row">
                      <div>
                        <input
                          checked={usersForDisplay.every(user => selectedUserIds.includes(user.id))}
                          type="checkbox"
                          onChange={e => toggleSelectAll(e.target.checked)}
                        />
                      </div>
                      <div>
                        Name
                      </div>
                    </div>
                  </th>
                  <th>Office</th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map(user => (
                  <UserTableRow
                    hidden={!usersForDisplay.includes(user)}
                    isSelected={selectedUserIds.includes(user.id)}
                    key={user.id}
                    officeName={officesById[user.companyOfficeId]?.name}
                    user={user}
                    onUserSelection={handleUserSelection}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <div className="d-flex justify-content-center mt-4">
            <UltimatePagination
              currentPage={page}
              totalPages={totalPages}
              onChange={setPage}
            />
          </div>
        </div>
      ) : (
        <div className="p-3 border w-100 text-center">
          No users found
        </div>
      )}
    </div>
  );
};

export default UserTable;
